import { Capacitor, registerPlugin } from '@capacitor/core';
import { authenticatedRequest } from './authApi';

// Keep the proxy on the runtime global so Fast Refresh/module re-evaluation
// does not ask Capacitor to register the same native plugin a second time.
const ORACLE_BILLING_PROXY_KEY = '__zodiacusOracleBillingProxy';
const runtimeGlobal = typeof window !== 'undefined' ? window : {};
const NativeOracleBilling = runtimeGlobal[ORACLE_BILLING_PROXY_KEY] || (runtimeGlobal[ORACLE_BILLING_PROXY_KEY] = registerPlugin('OracleBilling'));
const ORACLE_GOOGLE_PLAY_PRODUCT_ID = 'oracle_premium_monthly';

export function isAvailable() {
  return Boolean(Capacitor?.isNativePlatform?.() && Capacitor?.getPlatform?.() === 'android' && Capacitor?.Plugins?.OracleBilling);
}

async function purchaseOrRestore(method) {
  if (!isAvailable()) return { state: 'unavailable' };
  // Native Android owns the canonical product/base-plan selection. This bridge
  // intentionally accepts no caller-controlled product or base-plan input.
  const result = await NativeOracleBilling[method]();
  if (result?.state === 'pending') return { state: 'pending' };
  if (result?.state === 'canceled' || result?.canceled) return { state: 'canceled_by_user' };
  if (result?.state !== 'purchased' || !result?.purchaseToken) return { state: 'failed' };
  const verified = await authenticatedRequest('/api/oracle/billing/google/verify', { method: 'POST', body: JSON.stringify({ purchaseToken: result.purchaseToken, productId: ORACLE_GOOGLE_PLAY_PRODUCT_ID }) });
  if (verified?.entitlement?.status === 'pending') return { state: 'pending', entitlement: verified.entitlement };
  if (verified?.acknowledgementRequired === true) return { state: 'acknowledgement_pending', entitlement: verified.entitlement };
  return { state: verified?.entitlement?.hasAccess ? 'success' : 'failed', entitlement: verified?.entitlement || null };
}

async function restoreOraclePurchases() {
  if (!isAvailable()) return { state: 'unavailable', purchaseTokens: [] };
  const result = await NativeOracleBilling.restoreOraclePurchases();
  const purchaseTokens = Array.isArray(result?.purchaseTokens) ? result.purchaseTokens : [];
  if (!purchaseTokens.length) return { state: result?.pending ? 'pending' : 'none', purchaseTokens: [], entitlement: null };
  const verified = await authenticatedRequest('/api/oracle/billing/google/restore', { method: 'POST', body: JSON.stringify({ purchases: purchaseTokens.map((purchaseToken) => ({ purchaseToken, productId: ORACLE_GOOGLE_PLAY_PRODUCT_ID })) }) });
  if (verified?.entitlement?.status === 'pending') return { state: 'pending', purchaseTokens, entitlement: verified.entitlement, restored: verified.restored || [] };
  if ((verified?.restored || []).some((item) => item?.acknowledgementRequired === true)) return { state: 'acknowledgement_pending', purchaseTokens, entitlement: verified.entitlement, restored: verified.restored || [] };
  return { state: verified?.entitlement?.hasAccess ? 'success' : 'failed', purchaseTokens, entitlement: verified?.entitlement || null, restored: verified?.restored || [] };
}

async function acknowledgePurchase(purchaseToken) {
  if (!isAvailable()) return { state: 'unavailable' };
  return NativeOracleBilling.acknowledgeOraclePurchase({ purchaseToken });
}

export const oracleBillingClient = Object.freeze({
  isAvailable,
  purchaseOracleMonthly: () => purchaseOrRestore('purchaseOracleMonthly'),
  getProductDetails: () => (isAvailable() ? NativeOracleBilling.getOracleProductDetails() : Promise.resolve({ state: 'unavailable' })),
  restoreOraclePurchases,
  acknowledgePurchase,
});
